import React, { useEffect, useMemo, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import "./login.scss";
import LoginBcgImage from "../../assets/images/bg-login.png";
import BgForgotImage from "../../assets/images/forgot-bg.png";
import Welcome from "../../common/components/welcome/welcome";
import PasswordInputComponent from "../../common/components/password-input-component/password-input-component";
import { NavLink, useNavigate } from "react-router-dom";
import { routes } from "../../constants/routes";
import { useSetLoading } from "../../common/hooks/appearance";
import { handleLogin } from "../../common/routines/user-operations";
import { login } from "../../api/login";
import DisplayVersion from "../display-version/display-version";
import { initSessionTimers } from "../../common/session/sessionTimer";
import { jwtDecode } from "jwt-decode";
import { AlertComponent } from "../../common/components";

interface LoginProps {
  isLoggedIn?: boolean;
}

const Login: React.FC<LoginProps> = ({ isLoggedIn }) => {
  const initialValues = { username: "", password: "", rememberMe: false };
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<any | null>(null);
  const [alert, setAlert] = useState<{
    isOpen?: boolean;
    message?: any;
    type?: string;
  }>({});

  const navigate = useNavigate();

  const validationSchema = Yup.object({
    username: Yup.string().max(180).required("Username is required"),
    password: Yup.string().max(30).required("Password is required"),
    rememberMe: Yup.boolean(),
  });

  const onSubmit = async (values: typeof initialValues) => {
    setIsLoading(true);
    try {
      const response: any = await login(values);
      const accessToken = response?.data?.data.accessToken;
      const refreshToken = response?.data?.data?.refreshToken;
      const details = response?.data?.data;
      const firstTimeLogin = response?.data?.data?.userDetails?.firstTimeLogin;
      setIsLoading(false);

      if (accessToken && refreshToken) {
        localStorage.setItem("token", accessToken);
        localStorage.setItem("refreshToken", refreshToken);
        const decoded: any = jwtDecode(accessToken);
        if (decoded?.exp) {
          const expiry = decoded.exp * 1000;
          localStorage.setItem("expiryTime", expiry.toString());
        }
        handleLogin(details);
        localStorage.setItem("showFirstTimeLoginMsgs", "true");

        if (firstTimeLogin) {
          localStorage.setItem("firstTimeLogin", "true");
        }
        navigate(routes.DASHBOARD);
      }

      setIsLoading(false);
      initSessionTimers();
    } catch (error: any) {
      setIsLoading(false);
      if (error?.response) {
        setMessage({ message: error?.response?.data?.message, types: "error" });
      } else {
        setMessage({ message: "Something went wrong!", types: "error" });
      }
    }
  };

  useEffect(() => {
    const notification = localStorage.getItem("roleNotification");
    if (notification) {
      setAlert({ isOpen: true, message: notification, type: "error" });
      localStorage.removeItem("roleNotification");
    }
  }, []);

  const showLoading = useMemo(() => isLoading, [isLoading]);
  useSetLoading(showLoading);

  return (
    <div
      className="login-page-wrapper back-wrapper"
      style={{
        backgroundImage: `url(${BgForgotImage}), url(${LoginBcgImage})`,
        backgroundRepeat: "no-repeat, no-repeat",
        backgroundPosition: "center bottom, center bottom",
        backgroundSize: "100% 40%, 100% 40%",
      }}
    >
      <div className="login-card-wrapper">
        <div>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
          >
            {({ isSubmitting, setFieldValue, dirty, isValid }) => (
              <Form className="loginForm">
                <h2>Log In</h2>
                <div className="formBody">
                  <div className="field">
                    <Field
                      type="text"
                      name="username"
                      placeholder="Username *"
                      maxLength={180}
                      onChange={(e: any) => {
                        setMessage(null);
                        setFieldValue("username", e.target.value);
                      }}
                    />
                    <ErrorMessage
                      name="username"
                      component="div"
                      className="errorMessage"
                    />
                  </div>
                  <div className="passwordFieldOutlinedInputBox">
                    <PasswordInputComponent
                      fieldName="password"
                      placeholder="Password *"
                      optional={true}
                      onChange={(e: any) => {
                        setMessage(null);
                        setFieldValue("password", e.target.value);
                      }}
                      maxlength="30"
                      className="passwordFieldOutlinedInput"
                      sx={{
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#F7F7F7",
                          borderWidth: "0px",
                        },
                      }}
                    />
                  </div>
                  <button
                    type="submit"
                    className="submitButton"
                    disabled={isSubmitting || isLoading || !dirty || !isValid}
                  >
                    {isLoading ? "Logging in..." : "Login"}
                  </button>
                  {message?.message ? (
                    <div
                      className={`${message?.types === "error" ? `login-error-message` : `login-success-message`}`}
                    >
                      {message?.message === "Realm not enabled"
                        ? "This account has been deactivated. To reactivate your account, please contact the AFP team."
                        : message?.message}
                    </div>
                  ) : (
                    ""
                  )}

                  <div className="helperLinks">
                    <label></label>
                    <NavLink to={routes.FORGOTPASSWORD}>
                      Forgot Password?
                    </NavLink>
                  </div>
                  <div className="or-wrapper">
                    <hr />
                    <span>Or</span>
                    <hr />
                  </div>
                  <div className="description-item">
                    <span>Empower your Business with AFP</span>
                  </div>
                  <div className="contact-us">
                    <span>
                      <NavLink to={routes.REQUEST_ACCESS}>
                        Request Access
                      </NavLink>
                    </span>
                    <div className="divider"></div>
                    <span>
                      <NavLink to={routes.CONTACT_US}>Contact Us</NavLink>
                    </span>
                  </div>
                  <div className="version-info">
                    <DisplayVersion />
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </div>
        <div className="right-content-wraper">
          <Welcome />
        </div>
      </div>

      {alert?.isOpen && (
        <AlertComponent
          message={alert?.message || ""}
          type={"error"}
          open={alert?.isOpen}
          onClose={() => setAlert({})}
        />
      )}
    </div>
  );
};

export default Login;
